/**
 * @module lib/currency
 * Country detection, exchange rates, and currency display helpers for the customer frontend.
 */

/** @typedef {{ code: string, name: string }} Country */

/** Supported countries for geo detection and pricing UI. */
export const COUNTRIES = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'EG', name: 'Egypt' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'MX', name: 'Mexico' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CL', name: 'Chile' },
  { code: 'PE', name: 'Peru' },
  { code: 'RU', name: 'Russia' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'GR', name: 'Greece' },
  { code: 'TR', name: 'Turkey' },
  { code: 'IL', name: 'Israel' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'OTHER', name: 'Other' },
];

/** Map ISO country code to ISO currency code. */
const COUNTRY_TO_CURRENCY = {
  IN: 'INR', US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', DE: 'EUR', FR: 'EUR',
  IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', CH: 'CHF', AT: 'EUR', SE: 'SEK',
  NO: 'NOK', DK: 'DKK', FI: 'EUR', IE: 'EUR', NZ: 'NZD', JP: 'JPY', KR: 'KRW',
  CN: 'CNY', SG: 'SGD', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP', VN: 'VND',
  AE: 'AED', SA: 'SAR', ZA: 'ZAR', EG: 'EGP', NG: 'NGN', KE: 'KES', BR: 'BRL',
  AR: 'ARS', MX: 'MXN', CO: 'COP', CL: 'CLP', PE: 'PEN', RU: 'RUB', PL: 'PLN',
  CZ: 'CZK', HU: 'HUF', GR: 'EUR', TR: 'TRY', IL: 'ILS', PT: 'EUR', RO: 'RON'
};

// Cache for exchange rates
let ratesCache = null;
let ratesCacheTime = 0;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

// Cache for detected country (+ region availability from backend)
let detectedCountryCache = null;

/** Clear geo country cache (e.g. after admin changes — rarely needed in customer app). */
export function clearDetectedCountryCache() {
  detectedCountryCache = null;
}

// Derive backend base from the same env var used by the rest of the app,
// falling back to the production URL so currency detection always works.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.avyug.com';

/**
 * Detect user's country by asking the backend to resolve it server-side.
 * Direct browser calls to third-party geolocation APIs block CORS on free tiers,
 * so detection is proxied through our backend. Falls back to India when detection
 * fails or the country is not in the supported list.
 *
 * @returns {Promise<Country>}
 */
export async function detectUserCountry() {
  if (detectedCountryCache) {
    return detectedCountryCache;
  }

  const defaultCountry = { code: 'IN', name: 'India', serving: true, region_available: true };

  try {
    const response = await fetch(`${BACKEND_URL}/api/geo/country`, {
      signal: AbortSignal.timeout(5000),
    });

    if (response.ok) {
      const data = await response.json();
      const countryCode = (data.country_code || '').toUpperCase();
      const serving = data.serving ?? data.region_available ?? true;

      if (countryCode) {
        const country = COUNTRIES.find(c => c.code === countryCode);
        if (country && country.code !== 'OTHER') {
          detectedCountryCache = { ...country, serving, region_available: serving };
          return detectedCountryCache;
        }
        detectedCountryCache = {
          code: countryCode,
          name: countryCode,
          serving,
          region_available: serving,
        };
        return detectedCountryCache;
      }
    }
  } catch (e) {
    console.warn('Country detection failed:', e);
  }

  detectedCountryCache = defaultCountry;
  return defaultCountry;
}

/**
 * Fetch exchange rates from INR to all currencies (cached for one hour).
 *
 * @returns {Promise<Object<string, number>|null>} Rates keyed by lowercase currency code, or `null` on failure.
 */
async function fetchExchangeRates() {
  const now = Date.now();
  
  // Return cached rates if still valid
  if (ratesCache && (now - ratesCacheTime) < CACHE_DURATION) {
    return ratesCache;
  }

  // Try multiple CDN sources
  const sources = [
    'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/inr.json',
    'https://raw.githubusercontent.com/fawazahmed0/currency-api/1/latest/currencies/inr.json'
  ];

  for (const url of sources) {
    try {
      const response = await fetch(url, {
        signal: AbortSignal.timeout(5000)
      });
      
      // Handle rate limiting
      if (response.status === 429 || response.status === 403) {
        console.warn(`Rate limited by ${url}, trying next...`);
        continue;
      }
      
      if (!response.ok) continue;
      
      const data = await response.json();
      ratesCache = data.inr || data.INR || {};
      ratesCacheTime = now;
      return ratesCache;
    } catch (e) {
      console.warn(`Failed to fetch from ${url}:`, e.message);
      continue;
    }
  }
  
  // Return cached data even if stale (better than nothing)
  if (ratesCache) {
    console.warn('Using stale exchange rates cache');
    return ratesCache;
  }
  
  console.warn('All exchange rate sources failed');
  return null;
}

/**
 * Convert an amount from INR to a target currency using a rates table.
 *
 * @param {number} amountINR - Amount in INR.
 * @param {string} currencyCode - Target ISO currency code (e.g. `'USD'`).
 * @param {Object<string, number>} rates - Exchange rates object (lowercase currency keys).
 * @returns {{ amount: number, formatted: string, currency: string }|null}
 */
function convertFromINR(amountINR, currencyCode, rates) {
  if (!rates || !currencyCode || currencyCode === 'INR') {
    return null;
  }

  const rate = rates[currencyCode.toLowerCase()];
  if (!rate) {
    return null;
  }

  const convertedAmount = amountINR * Number(rate);
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(convertedAmount);

  return {
    amount: convertedAmount,
    formatted,
    currency: currencyCode.toUpperCase()
  };
}

/**
 * Resolve the ISO currency code for a country.
 *
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code.
 * @returns {string|null}
 */
function getCurrencyForCountry(countryCode) {
  return COUNTRY_TO_CURRENCY[countryCode] || null;
}

/**
 * Resolve a display name for a country code.
 *
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code.
 * @returns {string}
 */
function getCountryName(countryCode) {
  const country = COUNTRIES.find(c => c.code === countryCode);
  return country ? country.name : countryCode;
}

/**
 * Format an amount in a given ISO currency (no FX conversion).
 *
 * @param {number} amount - Numeric amount.
 * @param {string} [currencyCode='INR'] - ISO currency code.
 * @returns {string}
 */
export function formatCurrencyAmount(amount, currencyCode = 'INR') {
  const code = (currencyCode || 'INR').toUpperCase();
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: code === 'JPY' || code === 'KRW' ? 0 : 2,
  }).format(Number(amount) || 0);
}

/**
 * Build INR and optional local-currency price strings for display.
 *
 * @param {number} priceINR - Price in INR.
 * @param {string} countryCode - ISO country code.
 * @param {Object<string, number>} rates - Exchange rates object.
 * @returns {{ inr: string, local: string|null, localAmount: number|null, currency: string|null }}
 */
function getPriceDisplay(priceINR, countryCode, rates) {
  const inrFormatted = `₹${priceINR.toLocaleString('en-IN')} INR`;
  
  if (!countryCode || countryCode === 'IN' || countryCode === 'OTHER') {
    return { inr: inrFormatted, local: null, localAmount: null, currency: null };
  }

  const currency = COUNTRY_TO_CURRENCY[countryCode];
  if (!currency || !rates) {
    return { inr: inrFormatted, local: null, localAmount: null, currency: null };
  }

  const converted = convertFromINR(priceINR, currency, rates);
  if (!converted) {
    return { inr: inrFormatted, local: null, localAmount: null, currency: null };
  }

  return {
    inr: inrFormatted,
    local: converted.formatted,
    localAmount: converted.amount,
    currency: converted.currency
  };
}
